import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '@/App';
import axios from 'axios';
import ProBadge from '@/components/ProBadge';
import LearningInsights from '@/components/LearningInsights';
import ProUpgradeCard from '@/components/ProUpgradeCard';
import { AlertTriangle, CheckCircle, TrendingUp, Zap, Clock, Target, RefreshCw, Info } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLOR_MAP = {
  green:  { bg: 'rgba(34,197,94,0.12)',  border: 'rgba(34,197,94,0.3)',  text: '#22c55e', ring: '#22c55e' },
  yellow: { bg: 'rgba(234,179,8,0.12)',  border: 'rgba(234,179,8,0.3)',  text: '#eab308', ring: '#eab308' },
  orange: { bg: 'rgba(249,115,22,0.12)', border: 'rgba(249,115,22,0.3)', text: '#f97316', ring: '#f97316' },
  red:    { bg: 'rgba(239,68,68,0.12)',  border: 'rgba(239,68,68,0.3)',  text: '#ef4444', ring: '#ef4444' },
};

const REC_ICON = { warning: AlertTriangle, info: Info, success: CheckCircle };
const REC_COLOR = { warning: '#f97316', info: '#00D9FF', success: '#22c55e' };

const ScoreRing = ({ score, label, color, size = 140 }) => {
  const c = COLOR_MAP[color] || COLOR_MAP.green;
  const r = (size - 16) / 2;
  const circ = 2 * Math.PI * r;
  const dash = (score / 100) * circ;
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="rotate-[-90deg]">
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="10"/>
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={c.ring} strokeWidth="10"
            strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
            style={{ transition: 'stroke-dasharray 1s cubic-bezier(0.16,1,0.3,1)' }}/>
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-bold text-white">{score}</span>
          <span className="text-[10px] text-gray-400 mt-0.5">/100</span>
        </div>
      </div>
      <span className="text-sm font-semibold" style={{ color: c.text }}>{label}</span>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, sub, color = '#00D9FF' }) => (
  <div className="glass-card p-4 flex items-center gap-4">
    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
      style={{ background: `${color}18`, border: `1px solid ${color}30` }}>
      <Icon size={18} style={{ color }} />
    </div>
    <div>
      <p className="text-xs text-gray-400 mb-0.5">{label}</p>
      <p className="text-xl font-bold text-white">{value}</p>
      {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
    </div>
  </div>
);

const DailyBar = ({ day, mins }) => {
  const pct = Math.min(100, (mins / 600) * 100);
  const over = mins > 480;
  return (
    <div className="flex flex-col items-center gap-1.5 flex-1">
      <span className="text-[10px] text-gray-400">{Math.round(mins / 60)}h</span>
      <div className="w-full rounded-full overflow-hidden" style={{ height: 80, background: 'rgba(255,255,255,0.05)' }}>
        <div className="w-full rounded-full transition-all duration-700"
          style={{
            height: `${pct}%`,
            marginTop: `${100 - pct}%`,
            background: over
              ? 'linear-gradient(180deg, #f97316, #ef4444)'
              : 'linear-gradient(180deg, #00D9FF, #0EA5E9)',
          }}/>
      </div>
      <span className="text-[10px] text-gray-500 text-center">{new Date(day).toLocaleDateString('en',{weekday:'short'})}</span>
    </div>
  );
};

const ScheduleHealth = () => {
  const { auth } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [proLock, setProLock] = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const res = await axios.get(`${API}/health/schedule`, { headers: { Authorization: `Bearer ${auth.token}` } });
      setData(res.data);
    } catch (e) {
      if (e.response?.status === 402) setProLock(e.response.data.detail);
      else setError('Could not load schedule health. Try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (proLock) return <ProUpgradeCard feature="Schedule Health" message={proLock.message} />;

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#0A0E27,#050816)' }}>
      <div className="text-center">
        <div className="w-12 h-12 rounded-full border-2 border-t-transparent animate-spin mx-auto mb-4" style={{ borderColor: '#00D9FF', borderTopColor: 'transparent' }}/>
        <p className="text-gray-400">Analysing your schedule…</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#0A0E27,#050816)' }}>
      <div className="glass-card p-8 text-center max-w-sm">
        <AlertTriangle size={32} className="text-orange-400 mx-auto mb-3"/>
        <p className="text-white mb-4">{error}</p>
        <button onClick={load} className="teal-btn px-6 py-2 text-sm">Retry</button>
      </div>
    </div>
  );

  const c = COLOR_MAP[data.burnout_color] || COLOR_MAP.green;
  const dailyEntries = Object.entries(data.daily_load || {}).sort(([a],[b]) => a.localeCompare(b));

  return (
    <div className="min-h-screen pt-20 px-4 pb-12" style={{ background: 'linear-gradient(135deg,#0A0E27,#050816)' }}>
      <div className="max-w-4xl mx-auto">

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="heading-font text-2xl font-bold text-white flex items-center gap-2">Schedule Health <ProBadge testId="page-pro-badge-health"/></h1>
            <p className="text-gray-400 text-sm mt-1">Your weekly time analysis & burnout prediction</p>
          </div>
          <button onClick={load}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm text-gray-300 hover:text-white transition-colors"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <RefreshCw size={14}/> Refresh
          </button>
        </div>

        <LearningInsights />

        {/* Score cards */}
        <div className="grid grid-cols-2 gap-6 mb-8">
          <div className="glass-card p-6 flex flex-col items-center gap-4">
            <ScoreRing score={data.wellbeing_score} label="Wellbeing Score" color={data.burnout_color} />
            <p className="text-xs text-gray-400 text-center">Based on your task load, energy levels and completion rate</p>
          </div>
          <div className="glass-card p-6 flex flex-col items-center gap-4">
            <ScoreRing score={data.burnout_score} label={`Burnout Risk — ${data.burnout_label}`} color={data.burnout_color} size={140}/>
            <p className="text-xs text-center" style={{ color: c.text }}>
              {data.burnout_label === 'Healthy' && 'You\'re managing your time well. Keep it up.'}
              {data.burnout_label === 'Moderate' && 'Watch your load. Build in more breathing space.'}
              {data.burnout_label === 'High Risk' && 'You\'re approaching overload. Reschedule non-urgent tasks.'}
              {data.burnout_label === 'Critical' && 'Danger zone. Remove tasks from this week now.'}
            </p>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <StatCard icon={Clock} label="Hours This Week" value={`${Math.round(data.total_mins_week/60)}h`} sub={`${data.total_mins_week} min`} color="#00D9FF"/>
          <StatCard icon={Target} label="Completion Rate" value={`${data.completion_rate}%`} sub={`${data.task_counts.completed} done`} color="#22c55e"/>
          <StatCard icon={AlertTriangle} label="Overloaded Days" value={data.overload_days} sub="days over 8h" color={data.overload_days > 0 ? '#f97316' : '#22c55e'}/>
          <StatCard icon={Zap} label="High Energy Tasks" value={`${data.high_energy_ratio}%`} sub="of this week" color={data.high_energy_ratio > 60 ? '#ef4444' : '#a78bfa'}/>
        </div>

        {/* Task breakdown */}
        <div className="glass-card p-6 mb-8">
          <h2 className="text-sm font-semibold text-gray-300 mb-4 uppercase tracking-wider">Task Breakdown</h2>
          <div className="grid grid-cols-4 gap-3">
            {[
              { label: 'Scheduled', value: data.task_counts.scheduled, color: '#00D9FF' },
              { label: 'Completed', value: data.task_counts.completed, color: '#22c55e' },
              { label: 'Pending',   value: data.task_counts.pending,   color: '#eab308' },
              { label: 'Cancelled', value: data.task_counts.cancelled, color: '#6b7280' },
            ].map(({ label, value, color }) => (
              <div key={label} className="text-center p-3 rounded-xl" style={{ background: `${color}10`, border: `1px solid ${color}25` }}>
                <p className="text-2xl font-bold" style={{ color }}>{value}</p>
                <p className="text-xs text-gray-400 mt-1">{label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Daily load bars */}
        {dailyEntries.length > 0 && (
          <div className="glass-card p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wider">Daily Load This Week</h2>
              <div className="flex items-center gap-3 text-xs text-gray-500">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-cyan-400 inline-block"/> Normal</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-400 inline-block"/> Over 8h</span>
              </div>
            </div>
            <div className="flex items-end gap-3">
              {dailyEntries.map(([day, mins]) => <DailyBar key={day} day={day} mins={mins}/>)}
            </div>
          </div>
        )}

        {/* Recommendations */}
        <div className="glass-card p-6">
          <h2 className="text-sm font-semibold text-gray-300 mb-4 uppercase tracking-wider">Recommendations</h2>
          <div className="space-y-3">
            {data.recommendations.map((rec, i) => {
              const Icon = REC_ICON[rec.type] || Info;
              const color = REC_COLOR[rec.type] || '#00D9FF';
              return (
                <div key={i} className="flex items-start gap-3 p-3 rounded-xl"
                  style={{ background: `${color}0D`, border: `1px solid ${color}22` }}>
                  <Icon size={16} style={{ color, flexShrink: 0, marginTop: 2 }}/>
                  <p className="text-sm text-gray-300">{rec.text}</p>
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </div>
  );
};

export default ScheduleHealth;
